import {useCallback} from 'react'
import {useDispatch, useSelector} from 'react-redux'
import {
  selectProposalInfo,
  updateOption,
} from '../../../../../app-store/ProposalSlice'
import {calculateCouponsAmountForItem} from '../../../../../utils/coupon-helpers'
import {isFirstCycleFreeRampPlan} from '../../../../../features/CatalogGrid/utils/rampPricing'
import {useProposalAuthClient} from '../contexts/proposal-auth-context'
import {buildPaymentPreviewPayload} from '../../../../../features/CatalogGrid/utils/buildPaymentPreviewPayload'
import {applyPaymentPreview} from '../../../../../features/CatalogGrid/utils/applyPaymentPreview'

export function useClientPaymentPreview() {
  const dispatch = useDispatch()
  const client = useProposalAuthClient()
  const proposalInfo = useSelector(selectProposalInfo)

  const refreshClientPaymentPreview = useCallback(async () => {
    const {thryvId, country, options} = proposalInfo

    await Promise.all(
      (options ?? []).map(async option => {
        const rawCoupons = option.coupons ?? []
        const productCodesStr = (option.items ?? [])
          .map(i => i.productCode)
          .join(',')

        // Hydrate backend-shape coupons { couponCode, couponName } → full
        // coupon objects via orders/coupon/validate. Same for both paths.
        const needsHydration = rawCoupons.some(c => !c.code && c.couponCode)
        let resolvedCoupons = rawCoupons
        if (needsHydration) {
          resolvedCoupons = (
            await Promise.all(
              rawCoupons.map(async coupon => {
                if (coupon.code) return coupon
                const code = coupon.couponCode
                if (!code) return null
                try {
                  const resp = await client(
                    `orders/coupon/validate?couponCode=${code}&country=${country}&addonsString=${productCodesStr}`,
                  )
                  return resp?.isValid ? resp : null
                } catch {
                  return null
                }
              }),
            )
          ).filter(Boolean)
          dispatch(
            updateOption({
              displayOrder: option.displayOrder,
              changes: {coupons: resolvedCoupons},
            }),
          )
        }

        if (thryvId) {
          // Existing client: ooe-service proxies the call to Recurly using its
          // own credentials. thryvId is never sent by the client — the backend
          // injects it from the proposal DB after verifying the proposal JWT.
          const couponCodes = resolvedCoupons
            .filter(c => c.isValid)
            .map(c => c.code)
          const payload = buildPaymentPreviewPayload({
            items: option.items ?? [],
            country,
            coupons: couponCodes,
          })
          try {
            const preview = await client('proposal-auth/preview-payment', {
              data: payload,
            })
            const result = applyPaymentPreview(
              option.items ?? [],
              preview,
              country,
            )
            dispatch(
              updateOption({
                displayOrder: option.displayOrder,
                changes: result,
              }),
            )
            return
          } catch (err) {
            if (err.message !== 'Billing info missing on account') {
              // Keep existing amounts on error
              return
            }
            // Billing info missing — account exists but order never completed.
            // Fall through to new-client calculation so coupons still apply.
          }
        }

        // New client: no Recurly account yet — compute due-today amounts
        // client-side by subtracting coupon discounts from unitPrice.
        const validCoupons = resolvedCoupons.filter(c => c.isValid)
        const hasRampItems = (option.items ?? []).some(item =>
          isFirstCycleFreeRampPlan(item.productCode),
        )
        if (!validCoupons.length && !hasRampItems) return

        const updatedItems = (option.items ?? []).map(item => {
          if (item.alreadyIncluded) return {...item, dueTodayAmount: 0}
          const isAddon = item.productType === 'APP' && !!item.parentProduct
          const lineTotal =
            (item.unitPrice ?? item.price ?? 0) * (item.quantity ?? 1)
          const {discountAmount} = calculateCouponsAmountForItem(
            {
              code: item.productCode,
              prodType:
                item.productType === 'ONE_TIME_FEE'
                  ? 'oneTimeFee'
                  : isAddon
                  ? 'itemBasedAddon'
                  : 'plan',
              ...(isAddon && {planCode: item.parentProduct}),
              currency: {unitAmount: lineTotal},
              quantity: 1,
            },
            validCoupons,
          )
          const updatedAddons = (item.addons ?? []).map(addon => {
            const addonLineTotal =
              (addon.unitPrice ?? addon.unitAmount ?? addon.price ?? 0) *
              (addon.quantity ?? 1)
            const {discountAmount: addonDiscount} =
              calculateCouponsAmountForItem(
                {
                  code: addon.productCode,
                  prodType: 'itemBasedAddon',
                  planCode: item.productCode,
                  currency: {unitAmount: addonLineTotal},
                  quantity: 1,
                },
                validCoupons,
              )
            return {
              ...addon,
              dueTodayAmount: Math.max(addonLineTotal - addonDiscount, 0),
            }
          })
          const upfrontPrice = isFirstCycleFreeRampPlan(item.productCode)
            ? 0
            : lineTotal
          return {
            ...item,
            dueTodayAmount: Math.max(upfrontPrice - discountAmount, 0),
            ...(item.addons?.length && {addons: updatedAddons}),
          }
        })

        const dueTodayTotal = updatedItems.reduce(
          (sum, i) =>
            sum +
            i.dueTodayAmount +
            (i.addons || []).reduce(
              (aSum, addon) => aSum + (addon.dueTodayAmount ?? 0),
              0,
            ),
          0,
        )

        dispatch(
          updateOption({
            displayOrder: option.displayOrder,
            changes: {items: updatedItems, dueTodayTotal},
          }),
        )
      }),
    )
  }, [proposalInfo, client, dispatch])

  return {refreshClientPaymentPreview}
}
